import React, { useContext, useMemo, useState } from "react";
import "./friendPlayScreen.css";
import "./reviewPuzzleScreen.css";
import { DataLayerContext } from "../dataLayer/DataLayer";
import { AppBar } from "./components/appBar";
import { PageHeading } from "./components/pageHeading";
import { PuzzleBoardSpace } from "./components/puzzleBoardSpace";
import { PuzzleWord } from "./components/puzzleWord";
import { drawArrows } from "./components/canvas/drawArrows";
import { findWordInBoard } from "../gameLogic/findWordInBoard";
import { useObservableProperty } from "../observable/useObservableProperty";
import { SimpleDate } from "../dataLayer/SimpleDate";
import type { FoundWord } from "../dataLayer/backendApi";
import { compareFriendWords } from "./friendWordComparison";

function longDate(d: SimpleDate): string {
  return new Date(d.year, d.month - 1, d.day).toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

/** The friend's board with the selected word's path drawn on top. */
function FriendPlayBoard(props: { board: string[][]; selectedWord: string | undefined }) {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const rowCount = props.board.length;
  const colCount = props.board.length > 0 ? props.board[0].length : 0;

  React.useEffect(() => {
    if (canvasRef.current) {
      const spaces = props.selectedWord ? findWordInBoard(props.board, props.selectedWord) : [];
      drawArrows(canvasRef.current, spaces);
    }
  }, [props.board, props.selectedWord]);

  const rows: React.JSX.Element[] = [];
  for (let y = 0; y < rowCount; y++) {
    const cells: React.JSX.Element[] = [];
    for (let x = 0; x < colCount; x++) {
      cells.push(
        <div className="puzzleReviewBoardSpaceContainer" key={`sc_${x}_${y}`}>
          <PuzzleBoardSpace
            key={`s_${x}_${y}`}
            letter={props.board[y][x]}
            state="Rest"
            space={{ x, y }}
          />
        </div>,
      );
    }
    rows.push(
      <div className="puzzleReviewBoardRow" key={`row${y}`}>
        {cells}
      </div>,
    );
  }

  return (
    <div className="puzzleReviewContainer">
      <canvas className="puzzleReviewOverlayCanvas" width="200px" height="200px" ref={canvasRef} />
      <div className="puzzleReviewRowsContainer">{rows}</div>
    </div>
  );
}

export function FriendPlayScreen() {
  const dataLayer = useContext(DataLayerContext);
  const view = useObservableProperty(dataLayer.friendPlay);
  const [selectedWord, setSelectedWord] = useState<string | undefined>();
  const bodyRef = React.useRef<HTMLDivElement>(null);
  const onlyYouRef = React.useRef<HTMLDivElement>(null);
  const sharedRef = React.useRef<HTMLDivElement>(null);
  const missedRef = React.useRef<HTMLDivElement>(null);
  const comparisonId = React.useId();

  React.useEffect(() => {
    setSelectedWord(undefined);
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [view?.playerId, view?.puzzleId]);

  const onBack = () => dataLayer.navigateBack();

  const groupWordsByLength = (words: FoundWord[]) => {
    const buckets: FoundWord[][] = [];
    for (const w of words) {
      const len = w.word.length;
      (buckets[len] ??= []).push(w);
    }
    for (const bucket of buckets) {
      if (bucket) bucket.sort((a, b) => (a.word < b.word ? -1 : 1));
    }
    return buckets;
  };

  const wordComparison = useMemo(
    () => compareFriendWords(view?.play?.words ?? [], view?.ownWords ?? []),
    [view?.play?.words, view?.ownWords],
  );

  const toggleWord = (word: string) =>
    setSelectedWord((prev) => (prev === word ? undefined : word));

  const jumpToSection = (
    event: React.MouseEvent<HTMLAnchorElement>,
    target: HTMLDivElement | null,
  ) => {
    event.preventDefault();
    const body = bodyRef.current;
    if (!body || !target) return;
    target.focus({ preventScroll: true });
    body.scrollTo({
      top:
        body.scrollTop + target.getBoundingClientRect().top - body.getBoundingClientRect().top - 12,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    });
  };

  if (!view) {
    return (
      <div className="screen friendplay">
        <AppBar onBack={onBack} />
      </div>
    );
  }

  const play = view.play;
  const wordCount = play?.words.length ?? 0;
  const goldCount = play?.words.filter((w) => w.rating === "Gold").length ?? 0;
  const silverCount = play?.words.filter((w) => w.rating === "Silver").length ?? 0;
  const bronzeCount = play?.words.filter((w) => w.rating === "Bronze").length ?? 0;
  const heading = view.isSelf ? "Your words" : `${view.displayName}\u2019s words`;
  const comparisonRegions = [
    {
      key: "only-you",
      label: "Only you",
      title: "Words only you found",
      count: wordComparison.onlyYou.length,
      ref: onlyYouRef,
    },
    {
      key: "both",
      label: "Both",
      title: "Words you both found",
      count: wordComparison.shared.length,
      ref: sharedRef,
    },
    {
      key: "missed",
      label: "Only them",
      title: "Words you missed",
      count: wordComparison.missed.length,
      ref: missedRef,
    },
  ];

  const renderWordGroups = (words: FoundWord[]) =>
    groupWordsByLength(words).map((list, len) => {
      if (!list || list.length === 0) return null;
      return (
        <div key={`len${len}`}>
          <div className="regularHeader">
            {len} Letters ({list.length} {list.length === 1 ? "word" : "words"})
          </div>
          <div className="sectionContainer">
            {list.map((w) => (
              <PuzzleWord
                key={`w_${w.word}`}
                word={w.word}
                isFound={true}
                isSelected={w.word === selectedWord}
                onClick={() => toggleWord(w.word)}
              />
            ))}
          </div>
        </div>
      );
    });

  return (
    <div className="screen friendplay">
      <div className="friendplay__header">
        <AppBar onBack={onBack} />
        <PageHeading eyebrow={longDate(view.date)} title={heading} />
        {!view.loading && !view.error && play?.played && play.board.length > 0 && (
          <div className="friendplay__board">
            <FriendPlayBoard board={play.board} selectedWord={selectedWord} />
            <div className="friendplay__hint">Tap any word to spot it on the board</div>
          </div>
        )}
      </div>

      <div className="friendplay__body" ref={bodyRef}>
        {view.loading ? (
          <div className="friendplay__status">{"Loading words\u2026"}</div>
        ) : view.error ? (
          <div className="friendplay__status">
            {"We couldn\u2019t load these words. Please try again later."}
          </div>
        ) : !play || !play.played ? (
          <div className="friendplay__status">
            {view.isSelf
              ? "You didn\u2019t play this puzzle."
              : `${view.displayName} didn\u2019t play this puzzle.`}
          </div>
        ) : (
          <>
            {!view.isSelf && view.selfPlayed && (
              <nav className="friendplay__venn" aria-label="Jump to word comparison section">
                <svg viewBox="0 0 320 104" preserveAspectRatio="none" aria-hidden="true">
                  <defs>
                    <clipPath id={`${comparisonId}-left`}>
                      <ellipse cx="112" cy="52" rx="96" ry="42" />
                    </clipPath>
                  </defs>
                  <ellipse className="friendplay__venn-left" cx="112" cy="52" rx="96" ry="42" />
                  <ellipse className="friendplay__venn-right" cx="208" cy="52" rx="96" ry="42" />
                  <ellipse
                    className="friendplay__venn-shared"
                    cx="208"
                    cy="52"
                    rx="96"
                    ry="42"
                    clipPath={`url(#${comparisonId}-left)`}
                  />
                  <ellipse
                    className="friendplay__venn-edge friendplay__venn-edge--left"
                    cx="112"
                    cy="52"
                    rx="96"
                    ry="42"
                  />
                  <ellipse
                    className="friendplay__venn-edge friendplay__venn-edge--right"
                    cx="208"
                    cy="52"
                    rx="96"
                    ry="42"
                  />
                </svg>
                <div className="friendplay__venn-links">
                  {comparisonRegions.map((region) => (
                    <a
                      key={region.key}
                      href={`#${comparisonId}-${region.key}`}
                      aria-label={`${region.count} ${region.title.toLowerCase()}. Jump to section.`}
                      onClick={(event) => jumpToSection(event, region.ref.current)}
                    >
                      <span className="friendplay__venn-count">{region.count}</span>
                      <span className="friendplay__venn-label">{region.label}</span>
                    </a>
                  ))}
                </div>
              </nav>
            )}

            {play.scored && goldCount + silverCount + bronzeCount > 0 && (
              <div className="review__medals">
                <span className="mchip">
                  <span className="mdot g"></span>Gold {goldCount}
                </span>
                <span className="mchip">
                  <span className="mdot s"></span>Silver {silverCount}
                </span>
                <span className="mchip">
                  <span className="mdot b"></span>Bronze {bronzeCount}
                </span>
              </div>
            )}

            {view.isSelf ? (
              <>
                <div className="majorSectionHeader">
                  {`Found ${wordCount} ${wordCount === 1 ? "Word" : "Words"}`}
                </div>
                <div className="majorSectionContainer">{renderWordGroups(play.words)}</div>
              </>
            ) : (
              <>
                {view.selfPlayed && (
                  <>
                    <div
                      className="majorSectionHeader"
                      id={`${comparisonId}-only-you`}
                      ref={onlyYouRef}
                      tabIndex={-1}
                    >
                      Words only you found
                    </div>
                    <div className="majorSectionSubHeader">
                      {wordComparison.onlyYou.length === 0
                        ? `${view.displayName} found every word you found.`
                        : `You found ${wordComparison.onlyYou.length} ${
                            wordComparison.onlyYou.length === 1 ? "word" : "words"
                          } ${view.displayName} didn\u2019t.`}
                    </div>
                    {wordComparison.onlyYou.length > 0 && (
                      <div className="majorSectionContainer">
                        {renderWordGroups(wordComparison.onlyYou)}
                      </div>
                    )}

                    <div
                      className="majorSectionHeader"
                      id={`${comparisonId}-both`}
                      ref={sharedRef}
                      tabIndex={-1}
                    >
                      Words you both found
                    </div>
                    <div className="majorSectionSubHeader">
                      {wordComparison.shared.length === 0
                        ? `You and ${view.displayName} didn\u2019t find any of the same words.`
                        : `You both found ${wordComparison.shared.length} ${
                            wordComparison.shared.length === 1 ? "word" : "words"
                          }.`}
                    </div>
                    {wordComparison.shared.length > 0 && (
                      <div className="majorSectionContainer">
                        {renderWordGroups(wordComparison.shared)}
                      </div>
                    )}
                  </>
                )}
                <div
                  className="majorSectionHeader"
                  id={`${comparisonId}-missed`}
                  ref={missedRef}
                  tabIndex={-1}
                >
                  Words you missed
                </div>
                <div className="majorSectionSubHeader">
                  {!view.selfPlayed
                    ? `You didn\u2019t play this puzzle, so all of ${view.displayName}\u2019s words are shown here.`
                    : wordComparison.missed.length === 0
                      ? `You found every word ${view.displayName} found.`
                      : `${view.displayName} found ${wordComparison.missed.length} ${
                          wordComparison.missed.length === 1 ? "word" : "words"
                        } you didn\u2019t.`}
                </div>
                {wordComparison.missed.length > 0 && (
                  <div className="majorSectionContainer">
                    {renderWordGroups(wordComparison.missed)}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
